import { deleteData, getData, postData, updateData } from "@/request/api";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

/* ── Notifications inbox ── */
export const useNotifications = (enabled = true) => {
  return useQuery({
    queryKey: ["notifications-inbox"],
    enabled,
    queryFn: async () => {
      const res = await getData("notifications/inbox");
      return res.data;
    },
    refetchInterval: 60 * 1000, // poll every minute
    staleTime: 30 * 1000,
  });
};

export const useUnreadCount = (enabled = true) => {
  return useQuery({
    queryKey: ["notifications-unread"],
    enabled,
    queryFn: async () => {
      const res = await getData("notifications/inbox/unread-count");
      return res.data?.unread ?? 0;
    },
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
};

export const useMarkAllRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await updateData("notifications/inbox/read-all", {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications-inbox"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });
};

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await updateData(`notifications/inbox/${id}/read`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications-inbox"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });
};

/* ── Saved searches ── */
export const useSavedSearches = () => {
  return useQuery({
    queryKey: ["saved-searches"],
    queryFn: async () => (await getData("notifications/saved-searches")).data,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSavedSearch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) =>
      (await postData("notifications/saved-searches", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
  });
};

export const useDeleteSavedSearch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await deleteData(`notifications/saved-searches/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
  });
};
